// The vertical drag that closes a full-screen photo viewer, shared by react/shared/photo/usePhotoDrag.ts and interpals/image-lightbox.js.
// A plain module: webpack keeps react/ sources out of the legacy bundle. The thresholds are the spec of #5446.

import { prefersReducedMotion } from './reduced-motion.js';

// The share of the viewport height a vertical drag must pass to close the viewer.
const DISMISS_DISTANCE_RATIO = 0.15;
// The release speed, in px/ms, that closes the viewer at a shorter distance.
const DISMISS_VELOCITY = 0.8;
// The travel, in px, after which a drag commits to an axis.
const AXIS_LOCK_PX = 10;
// The duration of the fly-off and of the spring-back.
export const DISMISS_ANIMATION_MS = 200;
// The image scale at a drag of the full viewport height.
const DISMISS_MIN_SCALE = 0.85;
// The share of the viewport height at which the backdrop is clear.
const DISMISS_FADE_RATIO = 0.5;
// The last moves before the release that measure its speed.
const VELOCITY_WINDOW_MS = 100;

/** The axis of a drag, null until it passes AXIS_LOCK_PX on either axis. */
export function dragAxis(dx, dy) {
    if (Math.abs(dx) <= AXIS_LOCK_PX && Math.abs(dy) <= AXIS_LOCK_PX) return null;
    return Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
}

/** The samples inside the velocity window, with the move at `y`, time `t` (ms) appended. */
export function trackDrag(samples, y, t) {
    return [...samples.filter((sample) => t - sample.t <= VELOCITY_WINDOW_MS), { y, t }];
}

/** The vertical speed, in px/ms, of the moves inside the velocity window before `now`. A finger that rested has none. */
export function dragVelocity(samples, now) {
    const recent = samples.filter((sample) => now - sample.t <= VELOCITY_WINDOW_MS);
    if (recent.length < 2) return 0;
    const first = recent[0];
    const last = recent[recent.length - 1];
    return last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
}

/** Whether a released vertical drag closes the viewer: past the distance, or a flick in the direction of the drag. */
export function shouldDismiss(dy, velocity, height) {
    if (Math.abs(dy) > height * DISMISS_DISTANCE_RATIO) return true;
    return Math.abs(velocity) > DISMISS_VELOCITY && Math.sign(velocity) === Math.sign(dy);
}

/** The image transform of a vertical drag in progress. */
export function dismissTransform(dy, height) {
    const scale = 1 - (1 - DISMISS_MIN_SCALE) * Math.min(1, Math.abs(dy) / height);
    return `translateY(${dy}px) scale(${scale})`;
}

/** The backdrop opacity of a vertical drag in progress, from 1 at rest down to `floor`. */
export function dismissBackdropOpacity(dy, height, floor = 0) {
    return Math.max(floor, 1 - Math.abs(dy) / (height * DISMISS_FADE_RATIO));
}

/** The image transform at the end of the fly-off: one viewport height past the start, in the direction of the drag. */
export function dismissExitTransform(dy, height) {
    return `translateY(${Math.sign(dy) * height}px) scale(${DISMISS_MIN_SCALE})`;
}

/** A drag on a pinch-zoomed page pans the page. */
export function pinchZoomed() {
    return Boolean(window.visualViewport) && window.visualViewport.scale > 1;
}

/** The transition of a released drag: none under reduced motion. */
export function releaseTransition(property) {
    return prefersReducedMotion() ? 'none' : `${property} ${DISMISS_ANIMATION_MS}ms ease-out`;
}

/** The backdrop colour at `opacity` of a black backdrop whose class sets `alpha`. */
export function backdropColor(opacity, alpha) {
    return `rgba(0, 0, 0, ${opacity * alpha})`;
}
